import { Card } from "@/core/components/ui/card";
import { ScrollArea } from "@/core/components/ui/scroll-area";
import { Stack } from "@/core/components/ui/stack";
import { Typography } from "@/core/components/ui/typography";

const PrivacyPolicy = () => (
  <ScrollArea className="h-[calc(100vh-67px)] w-full px-8">
    <Card className="my-10 h-full w-full max-w-[1440px] p-6">
      <Stack className="max-w-[800px]" fullWidth>
        <Typography variant="titleW2">Privacy Policy</Typography>
        <Typography variant="titleW3">
          Your privacy is important to us
        </Typography>

        <Typography variant="bodyW1">
          Volca ("Volca", "we", "our" or "us") puts great efforts in making sure
          that the personal data processed by us is safe and used properly, and
          that our data practices are properly communicated to our customers,
          users and prospects.
        </Typography>

        <Typography variant="bodyW1">
          This Privacy Policy describes how we collect, store, use and disclose
          the following types of personal data:
        </Typography>

        <UnorderedList
          items={[
            {
              description:
                '(i) Customer Data: data relating to identifiable individuals ("Personal Data"), which we analyze, process and manage on our customers\' behalf ("Services"). Specifically, this is data which is contained in call recordings, transcripts, and other communications and content, as well as data submitted by our customers (including by connecting their systems with the Services);',
            },
            {
              description:
                '(ii) Volca User Data: account, contact and activity data relating to individuals who use the Volca platform, mobile application, and other tools and features provided by us as part of the Services (collectively – "Platform");',
            },
            {
              description:
                '(iii) Volca Website, CRM & Prospect Data: data relating to our customers, visitors of our website (www.govolca.com), participants at our events, and any other prospective customer or partner who visits or otherwise interacts with any of our websites, online ads and content, emails or communications under our control ("Sites").',
            },
          ]}
        />

        <Typography variant="bodyW1">
          Specifically, this Privacy Policy describes our practices regarding –
        </Typography>

        <UnorderedList
          items={[
            { description: "Data Collection & Processing" },
            { description: "Data Uses" },
            { description: "Data Location" },
            { description: "Data Retention" },
            { description: "Data Sharing" },
            { description: "Cookies and Tracking Technologies" },
            { description: "Communications" },
            { description: "Data Security" },
            { description: "Data Subject Rights" },
            { description: "Data Controller/Processor" },
            { description: "Additional Notice & Contact Details" },
          ]}
        />

        <Typography variant="bodyW1">
          If you are our customer, user, visitor or prospect ("you"), please
          read this Privacy Policy carefully and make sure that you fully
          understand and agree to it.
        </Typography>

        <Typography variant="bodyW1">
          You are not legally required to provide us with any personal data, and
          may do so (or avoid doing so) at your own free will. If you do not
          wish to provide us with your personal data, or to have it processed by
          us or any of our Service Providers (defined below), please avoid any
          interaction with us or use of our Services. If you are a user of the
          Platform on behalf of any of our customers, we suggest that you
          contact your account administrator with any questions.
        </Typography>

        <Typography variant="subtitleW1">
          1. Data Collection & Processing
        </Typography>

        <Typography variant="bodyW1">
          (i) Customer Data: Our Services enable Volca customers to run custom
          referral programs from their phones (collectively, "Customer Data").
        </Typography>

        <Typography variant="bodyW1">
          Customer Data may contain data which relates to identifiable
          individuals, such as the customer's sales representatives, prospects,
          and other parties either taking part in their communications or
          mentioned there.
        </Typography>

        <Typography variant="bodyW1">
          Volca processes Customer Data, and the personal data contained in it,
          strictly on our customer's behalf, in accordance with their reasonable
          instructions and as further stipulated in our Data Processing Addendum
          and other commercial agreements with such customer.
        </Typography>

        <Typography variant="bodyW1">
          Accordingly, to the extent that the EU's General Data Protection
          Regulation (GDPR), California Consumer Privacy Act (CCPA), UK Data
          Protection Act (UKDPA) or any similar laws apply, our customer will be
          deemed the 'data controller' (under the GDPR, UKDPA and similar laws)
          or 'business' (under the CCPA or similar laws) of such Customer Data;
          and Volca will be deemed the 'data processor' or 'service provider'
          when processing such data.
        </Typography>

        <Typography variant="bodyW1">‍</Typography>

        <Typography variant="bodyW1">
          (ii) Volca User Data: We collect and generate the following types of
          personal data concerning users of our Platform –user account
          information (e-mail address and, when applicable, hashed password);
        </Typography>

        <Typography variant="bodyW1">
          profile and contact information (name, title, team, company, e-mail
          and phone number, and additional information and media submitted by
          them, their teammates or their organization);
        </Typography>

        <Typography variant="bodyW1">
          Platform usage information (connectivity, technical and aggregated
          usage data, such as user agent, IP addresses, device data (like type,
          OS, device id, browser version, locale and language settings used),
          activity logs, and the cookies installed or utilized on their device;
          and
        </Typography>

        <Typography variant="bodyW1">
          direct interactions and communications with us (including recordings
          and transcripts of your calls and emails with us, e.g. for user
          enablement, support and training purposes).
        </Typography>

        <Typography variant="bodyW1">
          Whilst Volca processes such Volca User Data on its customers' behalf
          (where it is included in Customer Data), we also use it for our own
          purposes, as described in Section ‎2 below. Accordingly, to the extent
          applicable – our customer will be deemed the 'data controller' or
          'business' with respect to such data; Volca will also be deemed an
          independent and separate 'data controller' or 'business' with respect
          to such data; and with respect to those portions or copies of Volca
          User Data that we process on behalf of our customer – we will also be
          deemed a 'data processor' or 'service provider'.
        </Typography>

        <Typography variant="bodyW1">
          (iii) Volca Website, CRM & Prospect Data: We collect and generate the
          following types of personal data concerning our website visitors,
          customers and prospects –
        </Typography>

        <Typography variant="bodyW1">
          Website usage information (connectivity, technical and aggregated
          usage data, such as user agent, IP addresses, device data (like type,
          OS, device id, browser version, locale and language settings used),
          activity logs, session recordings, and the cookies and pixels
          installed or utilized on their device;
        </Typography>

        <Typography variant="bodyW1">
          Customer account information (contact, contractual and billing details
          concerning our customers, which may also contain the details of their
          internal focal persons who directly engage with Volca concerning their
          organizational account, e.g. the account administrators, billing
          contacts and authorized signatories on behalf of the customer; as well
          as the customer's needs and preferences, as identified to us or
          recognized through our engagement with them);
        </Typography>

        <Typography variant="bodyW1">
          Information concerning our customers and prospects (contact and
          business details, our communications with such customers and prospects
          (correspondences, call and video recordings, and analyses thereof), as
          well as any needs, preferences, attributes and insights relevant to
          our potential engagement).
        </Typography>

        <Typography variant="bodyW1">
          We collect such data either automatically, through the data subject's
          interaction with us or with our website, Platform or Services; or
          through third party services, social media, analytics tools, events we
          organize or participate in, and other business initiatives.
        </Typography>

        <Typography variant="subtitleW1">2. Data Uses</Typography>

        <Typography variant="bodyW1">
          Volca processes Customer Data, and the personal data contained in it,
          strictly on our customer's behalf, in accordance with their reasonable
          instructions and as further stipulated in our Data Processing Addendum
          and other commercial agreements with such customer.
        </Typography>

        <Typography variant="bodyW1">
          We use other types of Personal Data (Volca User Data and Volca
          Website, CRM & Prospect Data) as necessary for the performance of our
          Services; to comply with our legal and contractual obligations; and to
          support our legitimate interests in maintaining and improving our
          Services, e.g. in understanding how our Services are used and how our
          campaigns are performing, and gaining insights which help us dedicate
          our resources and efforts more efficiently; in marketing, advertising
          and selling our Services; providing customer service and technical
          support; and protecting and securing our customers, users, visitors,
          prospects, ourselves and our Services.
        </Typography>

        <Typography variant="bodyW1">
          If you reside or are using the Services in a territory governed by
          privacy laws under which "consent" is the only or most appropriate
          legal basis for the processing of Personal Data as described herein
          (in general, or specifically with respect to the types of Personal
          Data you expect or elect to process or have processed by or via the
          Services, e.g. 'special categories' under the GDPR), your acceptance
          of our Privacy Policy will be deemed as your consent to the processing
          of your Personal Data for all purposes detailed in this Policy. If you
          wish to revoke such consent, please contact us at{" "}
          <a href="mailto:support@volca.ai" className="underline">
            support@volca.ai
          </a>
          .
        </Typography>

        <Typography variant="bodyW1">
          We do not sell your personal information for the intents and purposes
          of the California Consumer Privacy Act (CCPA).
        </Typography>

        <Typography variant="bodyW1">
          Specifically, we use Volca User Data and Volca Website, CRM & Prospect
          Data for the following purposes:
        </Typography>

        <UnorderedList
          items={[
            {
              description: "To facilitate, operate, and provide our Services;",
            },
            { description: "To train our customer and user-facing staff;" },
            {
              description:
                "To authenticate the identity of our users, and to allow them access to our Services;",
            },
            {
              description:
                "To provide our customers and users with assistance and support;",
            },
            {
              description:
                "To gain a better understanding on how individuals use and interact with our Sites and Services, and how we could improve their and others' user experience, and continue improving our products, offerings and the overall performance of our Services;",
            },
            {
              description:
                "To facilitate and optimize our marketing campaigns, ad management and sales operations, and to manage and deliver advertisements for our products and services more effectively, including on other websites and applications. Such activities allow us to highlight the benefits of using our Services, and thereby increase your engagement and overall satisfaction with our Services. This includes contextual, behavioral and interests-based advertising based on your activity, preferences or other data available to us or to our business partners;",
            },
            {
              description:
                "To contact our customers, users and prospects with general or personalized service-related messages, as well as promotional messages that may be of specific interest to them (as further described in Section ‎‎7 below);",
            },
            {
              description:
                "To facilitate, sponsor and offer certain events, contests and promotions;",
            },
            {
              description:
                "To support and enhance our data security measures, including for the purposes of preventing and mitigating the risks of fraud, error or any illegal or prohibited activity;",
            },
            {
              description:
                "To create aggregated statistical data, inferred non-personal data, or anonymized or pseudonymized data (rendered non-personal and non-identifiable), which we or our business partners may use to provide and improve our respective services, or for any other purpose; and",
            },
            { description: "To comply with applicable laws and regulations." },
          ]}
        />

        <Typography variant="subtitleW1">3. Data Location</Typography>

        <Typography variant="bodyW1">
          We and our authorized Service Providers (defined below) maintain,
          store and process Personal Data in the United States of America and
          other locations, as reasonably necessary for the proper performance
          and delivery of our Services, or as may be required by law; provided
          that Customer Data may only be processed in such locations as
          permitted in our Data Processing Addendum and other commercial
          agreements with such customer.
        </Typography>

        <Typography variant="bodyW1">
          While privacy laws may vary between jurisdictions, Volca and its
          affiliates and Service Providers are each committed to protect
          Personal Data in accordance with this Privacy Policy and customary
          industry standards, regardless of any lesser legal requirements that
          may apply in their jurisdiction. Among others, we have ensured that
          all transfers of Personal Data which originate from the EEA and made
          at our direction or control are based on appropriate data transfer
          mechanisms as established under the GDPR.
        </Typography>

        <Typography variant="subtitleW1">4. Data Retention</Typography>

        <Typography variant="bodyW1">
          We retain Customer Data strictly on our customer's behalf, in
          accordance with their reasonable instructions and as further
          stipulated in our Data Processing Addendum and other commercial
          agreements with such customer.
        </Typography>

        <Typography variant="bodyW1">
          We retain Volca User Data and Volca Website, CRM & Prospect Data for
          as long as it is reasonably necessary in order to maintain and expand
          our relationship and provide you with our Services and offerings; in
          order to comply with our legal and contractual obligations; or to
          protect ourselves from any potential disputes (i.e. as required by
          laws applicable to log-keeping, records and bookkeeping, and in order
          to have proof and evidence concerning our relationship, should any
          legal issues arise following your discontinuance of use), all in
          accordance with our data retention policy.
        </Typography>

        <Typography variant="bodyW1">
          Please note that except as required by applicable law or our specific
          agreements with you, we will not be obligated to retain your Personal
          Data for any particular period, and we are free to securely delete it
          or restrict access to it for any reason and at any time, with or
          without notice to you. If you have any questions about our data
          retention policy, please contact us by e-mail at{" "}
          <a href="mailto:support@volca.ai" className="underline">
            support@volca.ai
          </a>
          .
        </Typography>

        <Typography variant="subtitleW1">5. Data Sharing</Typography>

        <Typography variant="bodyW1">
          ‍Legal Compliance: In exceptional circumstances, and except as
          stipulated otherwise in our Data Processing Addendum and other
          commercial agreements with you, we may disclose or allow government
          and law enforcement officials access to your Personal Data, in
          response to a subpoena, search warrant or court order (or similar
          requirement), or in compliance with applicable laws and regulations.
          Such disclosure or access may occur if we believe in good faith that:
          (a) we are legally compelled to do so; (b) disclosure is appropriate
          in connection with efforts to investigate, prevent, or take action
          regarding actual or suspected illegal activity, fraud, or other
          wrongdoing; or (c) such disclosure is required to protect our
          legitimate business interests, including the security or integrity of
          our products and services.
        </Typography>

        <Typography variant="bodyW1">
          Service Providers: We may engage selected third party companies and
          individuals to perform services complementary to our own. Such service
          providers include hosting and server co-location services,
          communications and content delivery networks (CDNs), data and cyber
          security services, billing and payment processing services, fintech
          services, fraud detection and prevention services, web analytics,
          e-mail distribution and monitoring services, session or activity
          recording services, remote access services, performance measurement,
          data optimization and marketing services, social and advertising
          networks, content providers, e-mail, voicemails, support and customer
          relation management systems, and our legal, financial and compliance
          advisors (collectively, "Service Providers").
        </Typography>

        <Typography variant="bodyW1">
          Our Service Providers shall each be deemed as a 'data processor' or
          'service provider' in circumstances where Volca assumes the role of
          'data controller'; and where Volca acts as the 'data processor' or
          'service provider' for our customer, the Service Provider shall be
          deemed as our 'sub-processor' or 'service provider' (as further
          described in Section ‎10 below).
        </Typography>

        <Typography variant="bodyW1">
          Accordingly, these Service Providers may have access to Volca User
          Data and Website, CRM & Prospect Data; and if we have expressly listed
          them in our Sub-Processor List, they may also have access to Customer
          Data — all depending on each of their specific roles and purposes in
          facilitating and enhancing our Services, and may only use it for such
          limited purposes as determined in our agreements with them.
        </Typography>

        <Typography variant="bodyW1">
          When our Service Providers contact you in order to offer or promote
          our Services, they may additionally interest you in their own
          services. Should you decide to engage in such activities with the
          Service Provider, please note that such engagement is beyond the scope
          of Volca's Terms and Privacy Policy and will therefore be covered by
          our Service Provider's Terms and Privacy Policy.
        </Typography>

        <Typography variant="bodyW1">
          Third Party Websites and Services: Our Sites and Platform may include
          links to third party websites, and integrations with third party
          services. Such websites and third party services, and any information
          you process, submit, transmit or otherwise use with such websites and
          third party services, are governed by such third party's terms and
          privacy practices and policies, and not by this Privacy Policy. We
          encourage you to carefully read the terms and privacy policies of such
          website and third party services.
        </Typography>

        <Typography variant="bodyW1">
          Sharing Personal Data with our Customers and other Users: Customer
          Data is typically shared and is available to the users belonging to
          such customer's account. Volca User Data is shared with the
          administrator of the customer's account to which such user belongs
          (including data and communications concerning such user's account). In
          such cases, sharing such data means that the administrator(s) or other
          users of the same account may access it on behalf of the customer, and
          will be able to monitor, process and analyze the Personal Data
          contained therein. This includes instances where you may contact us
          for help in resolving an issue specific to a team of which you are a
          member (and which is managed by the same customer).
        </Typography>

        <Typography variant="bodyW1">
          Please note that Volca is not responsible for and does not control any
          further disclosure, use or monitoring by or on behalf of the customer,
          that itself acts as the 'data controller' of such data (as further
          described in Section ‎10 below).
        </Typography>

        <Typography variant="bodyW1">
          Protecting Rights and Safety: We may share Personal Data with others
          if we believe in good faith that this will help protect the rights,
          property or personal safety of Volca, any of our users or customers,
          or any members of the general public.
        </Typography>

        <Typography variant="bodyW1">
          Volca Subsidiaries and Affiliated Companies: We may share Personal
          Data internally within our group, for the purposes described in this
          Privacy Policy. In addition, should Volca or any of its subsidiaries
          or affiliates undergo any change in control or ownership, including by
          means of merger, acquisition or purchase of substantially all or part
          of its assets, Personal Data may be shared with or transferred to the
          parties involved in such an event. If we believe that such change in
          control might materially affect your Customer Data or Personal Data
          then stored with us, we will notify you of this event and the choices
          you may have via e-mail or prominent notice on our Services.
        </Typography>

        <Typography variant="bodyW1">
          For the avoidance of doubt, Volca may share Volca User Data, Website,
          CRM & Prospect Data in additional manners, pursuant to your explicit
          approval, or if we are legally obligated to do so, or if we have
          successfully rendered such data non-personal, non-identifiable and
          anonymous. We may transfer, share or otherwise use non-personal and
          non-identifiable data at our sole discretion and without the need for
          further approval.
        </Typography>

        <Typography variant="subtitleW1">
          6. Cookies and Tracking Technologies
        </Typography>

        <Typography variant="bodyW1">
          We and our Service Providers use cookies and other technologies for
          performance, tracking, analytics and personalization purposes. We may
          share non-identifiable / aggregated extracts of such information with
          our partners for our legitimate business purposes.
        </Typography>

        <Typography variant="bodyW1">
          Cookies are packets of information sent to your web browser and then
          sent back by the browser each time it accesses the server that sent
          the cookie. Some cookies are removed when you close your browser
          session. These are the "Session Cookies". Some last for longer periods
          and are called "Persistent Cookies". We use both types.
        </Typography>

        <Typography variant="bodyW1">
          We use Persistent Cookies to remember your log-in details and make it
          easier for you to log-in the next time you access the Platform. We may
          use this type of cookies and Session Cookies for additional purposes,
          to facilitate the use of the Services' features and tools.
        </Typography>

        <Typography variant="bodyW1">
          Whilst we do not change our practices in response to a "Do Not Track"
          signal in the HTTP header from a browser or mobile application, you
          can manage your cookies preferences, including whether or not to
          accept them and how to remove them, through your browser settings.
          Please bear in mind that disabling cookies may complicate or even
          prevent you from using the Services.
        </Typography>

        <Typography variant="bodyW1">
          We also use analytics tools, including Google Analytics, Hotjar,
          Pylon, and UXCam. These tools help us understand users' behavior on
          our Sites and Platform, including by tracking page content, and
          click/touch, movements, scrolls and keystroke activities.
        </Typography>

        <Typography variant="bodyW1">
          Further information about the privacy practices of our analytics
          service providers is available at:
          www.google.com/policies/privacy/partners/;
          https://help.uxcam.com/hc/en-us/articles/360004158171;
          https://www.hotjar.com/legal/policies/privacy/;
          https://usepylon.com/privacy
        </Typography>

        <Typography variant="subtitleW1">7. Communications</Typography>

        <Typography variant="bodyW1">
          We engage in service and promotional communications, through e-mail,
          phone, SMS and notifications.
        </Typography>

        <Typography variant="bodyW1">
          Service Communications: We may contact you with important information
          regarding our Services. For example, we may send you notifications
          (through any of the means available to us) of changes or updates to
          our Services, billing issues, service changes, log-in attempts or
          password reset notices, etc. Our customers, and other users on the
          same customer account, may also send you notifications, messages and
          other updates regarding their or your use of the Services. Such
          notifications and messages may also be sent by them to their own
          customers (for example, invites, recordings or meeting summaries
          created through our Services). You can typically control your
          communications and notifications settings from your Volca User profile
          settings, or otherwise in accordance with the instructions included in
          the communications sent to you. Please note that you will not be able
          to opt-out of receiving certain service communications which are
          integral to your use (like password resets or billing notices).
        </Typography>

        <Typography variant="bodyW1">
          Promotional Communications: We may also notify you about new features,
          additional offerings, events, special opportunities or any other
          information we think you will find valuable, as our customer, user or
          prospect. We may provide such notices through any of the contact means
          available to us (e.g. phone, mobile or e-mail), through the Services,
          Platform or Sites, or through our marketing campaigns on any other
          sites or platforms.
        </Typography>

        <Typography variant="bodyW1">
          If you do not wish to receive such promotional communications, you may
          notify Volca at any time by sending an e-mail to:{" "}
          <a href="mailto:support@volca.ai" className="underline">
            support@volca.ai
          </a>
          . or by following the "unsubscribe", "stop", "opt-out" or "change
          e-mail preferences" instructions contained in the promotional
          communications you receive.
        </Typography>

        <Typography variant="subtitleW1">8. Data Security</Typography>

        <Typography variant="bodyW1">
          We and our hosting services implement systems, applications and
          procedures to secure your personal data, to minimize the risks of
          theft, damage, loss of information, or unauthorized access or use of
          information.
        </Typography>

        <Typography variant="bodyW1">
          These measures provide sound industry standard security. However,
          although we make efforts to protect your privacy, we cannot guarantee
          that our Sites, Platform or Services will be immune from any
          wrongdoings, malfunctions, unlawful interceptions or access, or other
          kinds of abuse and misuse.
        </Typography>

        <Typography variant="subtitleW1">9. Data Subject Rights</Typography>

        <Typography variant="bodyW1">
          Individuals have rights concerning their Personal Data. If you wish to
          exercise your privacy rights under any applicable law, including the
          EU General Data Protection Regulation (GDPR) or the California
          Consumer Privacy Act (CCPA), such as the right to request access to,
          and rectification or erasure of your Personal Data held with Volca, or
          to restrict or object to such Personal Data's processing, or to port
          such Personal Data, or the right to equal services and prices (each to
          the extent available to you under the laws which apply to you) –
          please contact us by e-mail at:{" "}
          <a href="mailto:support@volca.ai" className="underline">
            support@volca.ai
          </a>
          .
        </Typography>

        <Typography variant="bodyW1">
          Please note that when you ask us to exercise any of your rights under
          this policy or applicable law, we may need to ask you to provide us
          certain credentials to make sure that you are who you claim you are,
          to avoid disclosure to you of personal information related to others
          and to ask you to provide further information to better understand the
          nature and scope of data that you request to access. Such additional
          data will be then retained by us for legal purposes (e.g. as proof of
          the identity of the person submitting the request), in accordance with
          Section ‎‎4 above.
        </Typography>

        <Typography variant="bodyW1">
          We may redact from the data which we will make available to you, any
          personal data related to others.
        </Typography>

        <Typography variant="bodyW1">
          If you would like to make any requests or queries regarding Personal
          Data that we process on our customer's behalf, please contact the
          administrator of such customer's account directly. For example, if you
          are our customer's customer or prospect, or if you are a user of Volca
          on behalf of our customer, and wish to access, correct, or delete data
          processed by Volca on behalf of our customer, please direct your
          request to the relevant customer (who is the "data controller" of such
          data – see Section ‎‎10 below). Note that if you do contact us, we may
          share your communications with our customer and its account users; and
          that we may also share the contact details of the account
          administrator with individuals who sent us such a request or query
          relating to this account.
        </Typography>

        <Typography variant="subtitleW1">
          10. Data Controller/Processor
        </Typography>

        <Typography variant="bodyW1">
          Certain data protection laws and regulations, such as the GDPR or the
          CCPA, typically distinguish between two main roles for parties
          processing Personal Data: the "data controller" (or under the CCPA,
          "business"), who determines the purposes and means of processing; and
          the "data processor" (or under the CCPA, "service provider"), who
          processes the data on behalf of the data controller (or business).
          Below we explain how these roles apply to our Services, to the extent
          that such laws and regulations apply.
        </Typography>

        <Typography variant="bodyW1">
          Volca is the "data controller" of Volca Website, CRM & Prospect Data.
          With respect to such data, we assume the responsibilities of data
          controller (solely to the extent applicable under law), as set forth
          in this Privacy Policy. In such instances, our Service Providers
          processing such data will assume the role of "data processor".
        </Typography>

        <Typography variant="bodyW1">
          Volca is the "data processor" of Customer Data, which we process on
          behalf of our customer (who is the "data controller" of such data; and
          our Service Providers who process such Customer Data on our behalf are
          the "sub-processors" of such data.
        </Typography>

        <Typography variant="bodyW1">
          Volca is both a "data controller" and "data processor" of Volca User
          Data. Such data is processed by Volca for its own purposes (as
          described in Section ‎2 above), as an independent 'controller'; whilst
          those certain portions of it which are included in Customer Data will
          be processed by us on our customer's behalf, as a 'processor'.
        </Typography>

        <Typography variant="bodyW1">
          Accordingly, Volca processes Customer Data strictly in accordance with
          such customer's reasonable instructions and as further stipulated in
          our Data Processing Addendum and other commercial agreements with such
          customer. The customer, as controller of such data, will be
          responsible for meeting any legal requirements applicable to data
          controllers (such as establishing a legal basis for processing and
          responding to Data Subject Rights requests concerning the data they
          control).
        </Typography>

        <Typography variant="bodyW1">
          For the avoidance of doubt, each customer is solely responsible for
          providing adequate notice to their account users and customers whose
          data may be contained in Customer Data – including sufficient
          reference to the processing of their Personal Data via the Services,
          and any other information necessary to comply with all applicable
          privacy and data protection laws; and to obtain all approvals and
          consents from such individuals as required under such laws.
        </Typography>

        <Typography variant="subtitleW1">11. Additional Notices</Typography>

        <Typography variant="bodyW1">
          Updates and Amendments: We may update and amend this Privacy Policy
          from time to time by posting an amended version on our Services. The
          amended version will be effective as of the date it is published. We
          will provide prior notice if we believe any substantial changes are
          involved via any of the communication means available to us or via the
          Sites, Platform and/or Services. After such notice period, all
          amendments shall be deemed accepted by you.
        </Typography>

        <Typography variant="bodyW1">
          External Links: While our Sites, Platform and/or Services may contain
          links to other websites or services, we are not responsible for their
          privacy practices. We encourage you to pay attention when you leave
          our Sites, Platform and/or Services for the website or application of
          such third parties, and to read the privacy policies of each and every
          website and service you visit. This Privacy Policy applies only to our
          Sites, Platform and/or Services.
        </Typography>

        <Typography variant="bodyW1">
          Our Services are not designed to attract children under the age of 16:
          We do not knowingly collect Personal Data from children and do not
          wish to do so. If we learn that a person under the age of 16 is using
          the Sites, Platform and/or Services, we will attempt to prohibit and
          block such use and will make our best efforts to promptly delete any
          Personal Data stored with us with regard to such child. If you believe
          that we might have any such data, please contact us by e-mail at{" "}
          <a href="mailto:support@volca.ai" className="underline">
            support@volca.ai
          </a>
          .
        </Typography>

        <Typography variant="bodyW1">
          Questions, concerns or complaints: If you have any comments or
          questions regarding our Privacy Policy, or if you have any concerns
          regarding your Personal Data held with us, please contact Volca's
          support at{" "}
          <a href="mailto:support@volca.ai" className="underline">
            support@volca.ai
          </a>
          . If you are a GDPR-protected individual, you also have the right to
          lodge a complaint with an EU supervisory authority.
        </Typography>
      </Stack>
    </Card>
  </ScrollArea>
);

interface UnorderedListProps {
  items: {
    title?: string;
    description: string;
  }[];
}

const UnorderedList = ({ items }: UnorderedListProps) => (
  <ul className="list-disc pl-8">
    {items.map((item, index) => (
      <li key={item.title || index} className="mb-2 text-lg font-normal">
        {item.title && <span className="font-bold">{item.title}:</span>}{" "}
        {item.description}
      </li>
    ))}
  </ul>
);

export default PrivacyPolicy;
